import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTitle } from "../../providers/titleProvider";
import InvestNowStepper from "../ui/investNowStepper";

import { useGA4React } from "ga-4-react";
import RecallApplicationDialog from "../dialogs/recallApplication";
import DropdownInfoBlock from "../ui/feedback/dropdownInfoBlock";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import SpeakToAConsultantFeedback from "../dialogs/SpeakToAConsultantFeedback";

import {
  completeStep,
  resetSteps,
  setRequireHelp,
  updateStep,
} from "../../features/stepper.slice";
import { resetSubmition } from "../../features/submition/submition.slice";
import { Report, ReportPageView } from "../../libs/loggers";

import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import {
  resetSpeakToAConsultantSuccess,
  sendSpeakToAConsultant,
} from "../../features/speakToAConsultant/speakToAConsultant.slice";
import Input from "../ui/forms/fields/input";
import PrimarySelect from "../ui/forms/fields/select";
import PrimaryButton from "../ui/buttons/primaryButton";
import OrangeCircularProgress from "../ui/progress/circularProgress";
import { resetData } from "../../features/data/data.slice";
import { resetUser } from "../../features/user/user.slice";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    overflow: "hidden",
  },
  textButton: {
    marginLeft: "auto",
    marginRight: "auto",
    display: "flex",
    justifyContent: "center",
    padding: "16px",
    marginBottom: "16px",
    cursor: "pointer",
    textAlign: "center",
    width: "75%",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      paddingRight: "5px",
      paddingLeft: "5px",
    },
  },
  dialogImage: {
    width: "100px",
    marginRight: "16px",
    [theme.breakpoints.down("sm")]: {
      width: "60px",
    },
  },
}));

const schema = Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  surname: Yup.string().required("Surname is required"),
  currency: Yup.string().required(
    "Please select the currency you are looking to invest in?"
  ),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  phoneNumber: Yup.string()
    .matches(/^[0-9]+$/, "Phone number must be numeric")
    .min(10, "Phone number must be at least 10 digits")
    .required("Phone number is required"),

  lumpSum: Yup.number()
    .typeError("Investment amount must be a number")
    .min(0, "Lump sum cannot be negative")
    .test(
      "lumpSum-min",
      "Lump sum must be at least 50,000 (ZAR) or 5,000 (Other currencies)",
      function (value) {
        const { currency, monthlyContribution = 0 } = this.parent;

        if (currency === "zar" && monthlyContribution >= 1000) {
          return value === 0 || value >= monthlyContribution;
        }

        const lumpValue = currency === "zar" ? 50000 : 5000;
        return value >= lumpValue;
      }
    ),

  monthlyContribution: Yup.number()
    .typeError("Monthly contribution must be a number")
    .transform((value, originalValue) =>
      String(originalValue).trim() === "" ? 0 : value
    )
    .min(0, "Monthly contribution cannot be negative")
    .when("currency", {
      is: "zar",
      then: (schema) =>
        schema
          .test(
            "monthlyContribution-min",
            "Monthly Contribution must be at least 1,000",
            (value) => value === 0 || value >= 1000
          )
          .test(
            "valid-with-lumpSum",
            "Monthly Contribution must be at least 1,000",
            function (value) {
              const { lumpSum = 0 } = this.parent;
              if (lumpSum > 0 && lumpSum < 50000) {
                return true; // Don't show error on monthly contribution, only on lump sum
              }
              return lumpSum >= 50000 || value >= 1000;
            }
          ),
      otherwise: Yup.number().notRequired(), // No validation needed for other currencies
    }),

  investmentCheck: Yup.mixed().when("currency", {
    is: "zar",
    then: Yup.mixed().test(
      "investment-required",
      "Either Lump Sum must be at least 50,000 or Monthly Contribution must be at least 1,000",
      function () {
        const { lumpSum = 0, monthlyContribution = 0 } = this.parent;
        return lumpSum >= 50000 || monthlyContribution >= 1000;
      }
    ),
    otherwise: Yup.mixed().notRequired(), // Disable validation for other currencies
  }),
});

function ChoosePath() {
  const classes = useStyles();
  const { setTitle } = useTitle();
  const ga4React = useGA4React();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const stepper = useSelector((state) => state.stepper.value);
  const userId = useSelector((state) => state.submition.data?.userId);
  const submitionId = useSelector(
    (state) => state.submition.submition?.submitionId
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const success = useSelector(
    (state) => state.speakToAConsultant.speakToAConsultantSentSuccess
  );
  const loading = useSelector((state) => state.speakToAConsultant.loading);

  const [showDialog, setShowDialog] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "all",
    resolver: yupResolver(schema),
    defaultValues: {
      currency: "zar",
      lumpSum: 0,
      monthlyContribution: 0,
    },
  });

  const handleClose = () => {
    setDialogOpen(false);
    if (success) {
      navigate(stepper.steps[0].url);
      dispatch(resetSubmition());
      dispatch(resetData());
      dispatch(resetSteps());
      dispatch(resetUser());
    }
  };

  const watchCurrency = watch("currency");

  const onSubmit = (data) => {
    const eventInfo = {
      action: "click",
      category: "guidance",
      description: "I don't know what I want",
      value: {
        nextPage: "",
        prevPage: window.location.pathname.replace(/^\/|\/$/g, ""),
        step: stepper.active + 1,
        value: "I_dont_know",
      },
    };
    Report(eventInfo, userId, submitionId);
    dispatch(sendSpeakToAConsultant(data))
      .unwrap()
      .then(() => {
        setDialogOpen(true);
        reset();
      });
    setShowDialog(false);
  };
  useEffect(() => {
    console.log("Validation Errors:", errors);
  }, [errors]);

  const onNext = async (requireHelp) => {
    if (requireHelp) {
      setShowDialog(true);
      return;
    }

    const newStep = stepper.active + 1;
    const eventInfo = {
      action: "click",
      category: "guidance",
      description: requireHelp
        ? "I don't know what I want"
        : "I know what I want",
      value: {
        nextPage: stepper.steps[newStep].url,
        prevPage: window.location.pathname.replace(/^\/|\/$/g, ""),
        step: stepper.active + 1,
        value: requireHelp ? "I_dont_know" : "I_know",
      },
    };
    Report(eventInfo, userId, submitionId);
    dispatch(resetSubmition());
    ga4React.event("choose_path", `requireHelp: ${requireHelp}`, "path");
    dispatch(setRequireHelp(requireHelp));
    dispatch(updateStep(newStep));
    dispatch(completeStep(stepper.active));
    navigate(stepper.steps[newStep].url);
  };

  const handleNext = () => {
    onNext(false);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    ReportPageView(
      window.location.pathname.replace(/^\/|\/$/g, ""),
      userId,
      submitionId
    );
  }, []);

  useEffect(() => {
    setTitle("Choose Path");
  }, [setTitle]);

  useEffect(() => {
    if (watchCurrency !== "zar") {
      setValue("monthlyContribution", 0);
    }
  }, [watchCurrency, setValue]);

  const symbol =
    watchCurrency === "zar"
      ? "R"
      : watchCurrency === "usd"
      ? "$"
      : watchCurrency === "gbp"
      ? "£"
      : watchCurrency === "eur"
      ? "€"
      : "R";

  const theme = useTheme();
  const isLightMode = useMediaQuery("(prefers-color-scheme: light)");
  const mode = isLightMode ? "white" : "black";
  const small = useMediaQuery(theme.breakpoints.down("sm"));

  const stepperContent = () => {
    return (
      <Box className={classes.root}>
        <Typography pt={1} sx={{ marginBottom: "10px" }} variant="h4">
          Welcome to our Invest Now online process
        </Typography>
        <Typography variant={"body1"} sx={{ marginBottom: "10px" }}>
          Find an investment solution to help achieve your financial goals.
          <br />
          We use a 5-step paperless process, which makes investing with us
          secure, simple and smooth.
        </Typography>

        <DropdownInfoBlock
          defaultOpen={false}
          title={small ? "More information" : "More information on the process"}
          listOnly={true}
          content="
           <ul>
            <li>
              In step 4, you will be required to upload your FICA documents. Please have your proof of identity, latest bank account statement and proof of address ready.
            </li>
            <li>
              In step 5, once you click 'Submit', you will receive an email from <a href='mailto:clientservices@fairtreeinvest.com'>clientservices@fairtreeinvest.com</a>. The email will contain a secure link to your application, which you can digitally sign.
            </li>
            <li>
             Once signed, our consultants will process your application and contact you.
            </li>
            <li>
              A copy of all documents will be emailed to you for records.
            </li>
          </ul> "
        />

        <Box textAlign={"center"}>
          <Typography sx={{ paddingTop: "10px" }} variant="h4">
            Let's Begin.
          </Typography>
          <img
            width={140}
            src={`https://s3.af-south-1.amazonaws.com/s3.fairtree.public/invest+now+icons/Iconography_${mode}_Target+achieved.png`}
            alt="target"
          />
        </Box>

        <Paper className={classes.textButton} onClick={() => onNext(false)}>
          <Typography variant="h6">
            I <span style={{ color: "#CC6323" }}>know</span> what fund I want.
          </Typography>
        </Paper>
        <Paper
          sx={{ mt: 2 }}
          className={classes.textButton}
          onClick={() => onNext(true)}
        >
          <Typography variant="h6">
            I <span style={{ color: "#CC6323" }}> don't know </span> what fund I
            want <span style={{ color: "#CC6323" }}>yet.</span>
          </Typography>
        </Paper>

        <RecallApplicationDialog />
      </Box>
    );
  };

  return (
    <>
      <InvestNowStepper
        selectedStep={4}
        getStepContent={() => stepperContent()}
        checkValidation={() => {}}
        onNext={() => handleNext()}
      />

      <Dialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box padding={2}>
          <img
            src={`https://s3.af-south-1.amazonaws.com/s3.fairtree.public/invest+now+icons/Iconography_${mode}_Real+relationships.png`}
            alt="Vehicle Icon"
            className={classes.dialogImage}
          />
          <Typography variant={"h3"} fontWeight={500}>
            Speak to a Consultant
          </Typography>
        </Box>
        <IconButton
          size="large"
          aria-label="close"
          onClick={() => setShowDialog(false)}
          sx={{ position: "absolute", top: 8, right: 8 }}
        >
          <CloseIcon fontSize="large" />
        </IconButton>
        <DialogContent>
          {/* <FormHelperText style={{ color: "#CC6323" }}>
            To start an investment, you will need a minimum monthly contribution
            of <strong>R1 000</strong> or a lump sum investment of{" "}
            <strong>R50 000</strong>.
            <br /> For more information on our funds or how to invest, please
            complete the form, and one of our consultants will contact you
            shortly.
          </FormHelperText> */}

          <Typography variant="body1">
            If you are uncertain about how to invest or would like help
            investing in one of our funds, we would love to assist you. Please
            complete this form and one of our consultants will contact you.
          </Typography>
          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit(onSubmit)}
            sx={{ mt: 2 }}
          >
            <Input
              label="First Name"
              required
              fullWidth
              margin="dense"
              {...register("firstName")}
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
            />
            <Input
              label="Surname"
              fullWidth
              required
              margin="dense"
              {...register("surname")}
              error={!!errors.surname}
              helperText={errors.surname?.message}
            />
            <Input
              label="Email Address"
              fullWidth
              required
              margin="dense"
              {...register("email")}
              error={!!errors.email}
              helperText={errors.email?.message}
            />
            <Input
              label="Phone Number"
              fullWidth
              required
              margin="dense"
              {...register("phoneNumber")}
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber?.message}
            />
            <PrimarySelect
              name="currency"
              required
              control={control}
              error={!!errors.currency}
              helperText={errors.currency?.message}
              label="Which currency are you looking to invest in?"
              fullWidth
            >
              <MenuItem value="zar">ZAR (R)</MenuItem>
              <MenuItem value="usd">USD ($)</MenuItem>
              <MenuItem value="gbp">GBP (£)</MenuItem>
              <MenuItem value="eur">EUR (€)</MenuItem>
            </PrimarySelect>

            <Typography sx={{ mt: 1, mb: 1 }}>Investment Amount</Typography>
            <Input
              type="number"
              label="Lump Sum"
              fullWidth
              name="lumpSum"
              symbol={symbol}
              control={control}
              {...register("lumpSum")}
              error={!!errors.lumpSum}
              helperText={
                errors.lumpSum ||
                (!watch("lumpSum") && !watch("monthlyContribution")) ? (
                  <span
                    style={{ color: errors.lumpSum ? undefined : "#CC6323" }}
                  >
                    {errors.lumpSum
                      ? errors.lumpSum.message
                      : `You will need a minimum lump sum investment of ${symbol} ${
                          watchCurrency === "zar" ? "50,000" : "5,000"
                        }.
                        `}
                  </span>
                ) : null
              }
            />

            {symbol === "R" && (
              <Input
                type="number"
                label="Monthly Contribution"
                fullWidth
                name="monthlyContribution"
                symbol={symbol}
                control={control}
                {...register("monthlyContribution")}
                error={!!errors.monthlyContribution}
                helperText={
                  errors.monthlyContribution ||
                  (!watch("lumpSum") && !watch("monthlyContribution")) ? (
                    <span
                      style={{
                        color: errors.monthlyContribution
                          ? undefined
                          : "#CC6323",
                      }}
                    >
                      {errors.monthlyContribution
                        ? errors.monthlyContribution.message
                        : "You will need a minimum monthly contribution of R1 000."}
                    </span>
                  ) : null
                }
              />
            )}

            <Box display="flex" justifyContent="flex-end">
              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{ mt: 2 }}
              >
                Submit
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <SpeakToAConsultantFeedback
        open={dialogOpen}
        onClose={handleClose}
        loading={loading}
        success={success}
      />
    </>
  );
}

export default ChoosePath;
